import { useEffect, useRef } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';

type ConfirmFn = () => Promise<boolean>;

// Module-level flag: survives component unmount/remount during navigation commit.
// Set to true before blocker.proceed() so that the newly-mounted destination
// component's blocker predicate returns false for the in-flight navigation.
// Reset to false once the destination location stabilises (useEffect fires).
let proceedingNavigation = false;

export function useConfirmExit(confirmFn: ConfirmFn, shouldBlock: boolean) {
  const isConfirmingRef = useRef(false);
  const location = useLocation();

  // Reset both guards once the new location is committed.
  useEffect(() => {
    proceedingNavigation = false;
    isConfirmingRef.current = false;
  }, [location.pathname]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !proceedingNavigation &&
      shouldBlock &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked' && !isConfirmingRef.current) {
      isConfirmingRef.current = true;

      confirmFn()
        .then((confirmed) => {
          if (confirmed) {
            proceedingNavigation = true;
            blocker.proceed();
            // isConfirmingRef stays true until location changes — prevents
            // re-triggering if the effect fires again during navigation commit.
          } else {
            blocker.reset();
            isConfirmingRef.current = false;
          }
        })
        .catch(() => {
          blocker.reset();
          isConfirmingRef.current = false;
        });
    }
  }, [blocker, confirmFn]);
}
